import { useEffect, useState } from 'react'
import { api } from 'core/api/ConnectionManager'
import helpersDigitalSignages from 'core/helpers/_digitalSignages'
import moment from 'moment'

export interface INotificationEvent {
    event: string
    emails?: string[] | null
}

export interface INotificationOptions {
    offlineTime?: string
    screenOffTime?: string
}

export interface IState {
    startTime: string
    endTime: string
    options: INotificationOptions
    events: INotificationEvent[]
    applyRecursively: boolean
    timeoutS: string
}

export interface IDisplaysNotificationsProps {
    selectedInfo: {
        id: number
        type: string
    } | null
    closeModal: () => void
    notificationsEmail: string[]
}

interface INotificationsSettingsResponse {
    startTime: string
    endTime: string
    options?: INotificationOptions
    events?: INotificationEvent[]
    applyRecursively?: boolean
    timeoutS?: string | number
}

interface IUpdateNotificationsSettingsRequest {
    options: INotificationOptions
    startTime: string
    endTime: string
    applyRecursively: boolean
    events: INotificationEvent[]
    timeoutS: number
    groupId?: number | null
    digitalSignageId?: number | null
}

type NotificationTimeField = 'startTime' | 'endTime' | keyof INotificationOptions

const getIdKeyByType = (selectedInfo: IDisplaysNotificationsProps['selectedInfo']): 'groupId' | 'digitalSignageId' => {
    return helpersDigitalSignages.isFolder(selectedInfo) ? 'groupId' : 'digitalSignageId'
}

export const DisplaysNotificationsMethods = (p_: IDisplaysNotificationsProps) => {
    const [state, setState] = useState<IState>({
        startTime: moment().startOf('day').format('HH:mm:ss'),
        endTime: moment().endOf('day').format('HH:mm:ss'),
        options: {},
        events: [],
        applyRecursively: false,
        timeoutS: '10800',
    })

    useEffect(() => {
        const params: Pick<IUpdateNotificationsSettingsRequest, 'groupId' | 'digitalSignageId'> = {}
        params[getIdKeyByType(p_.selectedInfo)] = p_.selectedInfo && p_.selectedInfo.id

        api.send<object, INotificationsSettingsResponse[]>('getNotificationsSettingsForDsOrGroup', params).then(
            (res) => {
                if (!res || !res.length) {
                    return
                }

                const result = { ...res[0] }

                if (result.startTime && result.endTime) {
                    const currentDate = moment().format('YYYY-MM-DD')
                    const startTime = moment.utc(`${currentDate} ${result.startTime}`).local().format('HH:mm:ss')
                    const endTime = moment.utc(`${currentDate} ${result.endTime}`).local().format('HH:mm:ss')
                    result.startTime = startTime
                    result.endTime = endTime
                }

                setState((prevState) => ({
                    ...prevState,
                    ...result,
                    options: result.options || prevState.options,
                    events: result.events || prevState.events,
                    timeoutS: result.timeoutS !== undefined ? result.timeoutS.toString() : prevState.timeoutS,
                }))
            }
        )
    }, [p_.selectedInfo])

    const checkedType = (checked: boolean, type: string) => {
        setState((prevState) => {
            if (type === 'deviceOfflineTime' || type === 'screenOffTimesDevice') {
                const optionKey = type === 'deviceOfflineTime' ? 'offlineTime' : 'screenOffTime'
                const options = { ...prevState.options }

                if (checked) {
                    options[optionKey] = '00:00:00'
                } else {
                    delete options[optionKey]
                }

                return { ...prevState, options }
            }

            const events = checked
                ? prevState.events.some((event) => event.event === type)
                    ? prevState.events
                    : [...prevState.events, { event: type, emails: [] }]
                : prevState.events.filter((event) => event.event !== type)

            return { ...prevState, events }
        })
    }

    const getCheckedValue = (type: string) => {
        return state.events.some((item) => item.event === type)
    }

    const getCheckedRightEventsValue = (type: string) => {
        const optionKey = type === 'deviceOfflineTime' ? 'offlineTime' : 'screenOffTime'

        return Object.prototype.hasOwnProperty.call(state.options, optionKey)
    }

    const setTime = (value: string, type: NotificationTimeField) => {
        const normalizedValue = moment(value, 'HH:mm:ss', true).isValid() ? value : moment().format('HH:mm:ss')

        setState((prevState) => {
            if (type === 'offlineTime' || type === 'screenOffTime') {
                return {
                    ...prevState,
                    options: {
                        ...prevState.options,
                        [type]: normalizedValue,
                    },
                }
            }

            return { ...prevState, [type]: normalizedValue }
        })
    }

    const applySettings = () => {
        const { closeModal } = p_
        const currentDate = moment().format('YYYY-MM-DD')
        const startTime = moment(`${currentDate} ${state.startTime}`).utc().format('HH:mm:ss')
        const endTime = moment(`${currentDate} ${state.endTime}`).utc().format('HH:mm:ss')
        const options = state.options

        const data: IUpdateNotificationsSettingsRequest = {
            options,
            startTime,
            endTime,
            applyRecursively: state.applyRecursively,
            events: state.events,
            timeoutS: 0,
        }

        data[getIdKeyByType(p_.selectedInfo)] = p_.selectedInfo && p_.selectedInfo.id

        let timeoutS: string = state.timeoutS.toString()

        if (!timeoutS.length) {
            timeoutS = '0'
        }

        data.timeoutS = parseInt(timeoutS, 10)

        api.send<IUpdateNotificationsSettingsRequest, unknown>('updateNotificationsSettingsForDsOrGroup', data).then(
            () => {
                closeModal()
            }
        )
    }

    return {
        state,
        setState,
        checkedType,
        getCheckedValue,
        getCheckedRightEventsValue,
        setTime,
        applySettings,
    }
}
